import { postJSON } from "../api.js";
import { showReportModal } from "../app/commands.js";
import { getCurrentTab, renderTab, showTab } from "../app/shell.js";
import { esc, fmtMoney } from "../ui/format.js";
import { uiGhostPanel, uiMoney, uiPays } from "../ui/kit.js";
import { cinemaBoardVote } from "../ui/cinema.js";
import { toast } from "../ui/modals.js";
import { sfx } from "../app/audio.js";

/**
 * CEO layer (MASTER_PROMPT Phase 2): CEO Inbox, goal ladder, delegation dial,
 * progressive unlocks and the month-close summary.
 *
 * Loaded after app.js and built on its global functions ($, esc, fmtMoney,
 * toast, postJSON, refreshAll, renderTab, getCurrentTab…). It wraps the existing
 * renderers instead of adding more code to app.js. Every number shown comes
 * from the backend: /api/inbox, /api/goals, /api/unlocks, /api/delegation.
 */

const CEO_API = "/api";
const CEO_SHOW_ALL_KEY = "ges_show_all_tabs";
let ceoUnlocks = null;

async function ceoGet(path) {
  const res = await fetch(`${CEO_API}${path}`);
  if (!res.ok) throw new Error(`GET ${path} failed`);
  return res.json();
}

// ---------------------------------------------------------------- CEO Inbox

function inboxCardHtml(card) {
  const rec = card.recommended_option;
  const advisor = card.advisor
    ? `<div class="inbox-advisor">💬 ${esc(card.advisor)} (${esc(shortRole(card.advisor_role))}) recommends
         <strong>${esc((card.options.find((o) => o.key === rec) || {}).label || rec)}</strong></div>`
    : "";
  const options = card.options.map((o) => `
      <button type="button" class="inbox-option${o.key === rec ? " recommended" : ""}" data-card="${card.id}" data-option="${esc(o.key)}">
        <span class="inbox-option-label">${esc(o.label)}</span>
        <span class="inbox-option-detail">${esc(o.detail || "")}</span>
        ${o.cost > 0 ? `<span class="inbox-option-cost">Costs ${fmtMoney(o.cost)}</span>${uiPays(o)}` : ""}
      </button>`).join("");
  return `
    <div class="inbox-card" data-department="${esc(card.department)}">
      <div class="inbox-card-dept">${esc(departmentLabel(card.department))}</div>
      <div class="inbox-card-title">${esc(card.title)}</div>
      <div class="inbox-card-body">${esc(card.body)}</div>
      ${advisor}
      <div class="inbox-options">${options}</div>
      ${inboxGhostHtml(card)}
    </div>`;
}

/** Ghost Futures on a card: forecast any option that is a real company move. */
function inboxGhostHtml(card) {
  const opts = card.options.filter((o) => o.forecastable);
  if (!opts.length) return "";
  return `<div class="inbox-ghost">
      <span>👻 See the future of:</span>
      ${opts.map((o) => `<button type="button" class="inbox-ghost-btn" data-ghost-card="${card.id}" data-option="${esc(o.key)}">${esc(o.label)}</button>`).join("")}
    </div>
    <div class="sx-forecast inbox-ghost-out" id="inbox-ghost-${card.id}"></div>`;
}

function inboxRecentHtml(recent) {
  const done = recent.filter((c) => c.status === "resolved").slice(0, 4);
  if (!done.length) return "";
  return `<div class="inbox-recent">${done.map((c) => {
    const chosen = (c.options.find((o) => o.key === c.chosen_option) || {}).label || c.chosen_option;
    const who = c.resolved_by && c.resolved_by !== "you" ? `🤖 ${esc(c.resolved_by)}` : "You";
    return `<div class="inbox-recent-row"><span>${who} · ${esc(c.title)} → <strong>${esc(chosen)}</strong></span>
            <span class="inbox-recent-result">${esc(c.result || "")}</span></div>`;
  }).join("")}</div>`;
}

const DEPARTMENT_LABELS = {
  finance: "Finance", product: "Product", market: "Deals & Market", risk: "Risk & Legal",
  people: "People", strategy: "Strategy & Corp Dev", government: "Government & Expansion",
};
function departmentLabel(key) { return DEPARTMENT_LABELS[key] || key; }
function shortRole(role) { const m = /\(([^)]+)\)/.exec(role || ""); return m ? m[1] : (role || ""); }

async function renderInboxInto(host) {
  let box;
  try { box = await ceoGet("/inbox"); } catch (e) { host.innerHTML = ""; return; }
  const open = box.open || [];
  host.innerHTML = `
    <div class="inbox-head">
      <h3 class="inbox-title">📥 CEO Inbox</h3>
      <span class="inbox-count">${open.length ? `${open.length} decision${open.length > 1 ? "s" : ""} waiting` : "All clear this month"}</span>
    </div>
    <div class="inbox-cards">${open.map(inboxCardHtml).join("") || `<p class="inbox-empty">Nothing needs you right now — press Next Month when you're ready.</p>`}</div>
    ${inboxRecentHtml(box.recent || [])}`;
  host.querySelectorAll("[data-ghost-card]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const card = open.find((c) => String(c.id) === btn.dataset.ghostCard);
      const opt = card && card.options.find((o) => o.key === btn.dataset.option);
      if (!opt) return;
      btn.parentElement.querySelectorAll("[data-ghost-card]").forEach((b) => b.classList.toggle("on", b === btn));
      uiGhostPanel(host.querySelector(`#inbox-ghost-${card.id}`)).run(opt.action);
    });
  });
  host.querySelectorAll(".inbox-option").forEach((btn) => {
    btn.addEventListener("click", async () => {
      host.querySelectorAll(".inbox-option").forEach((b) => { b.disabled = true; });
      try {
        const res = await postJSON(`/inbox/${btn.dataset.card}/resolve`, { option: btn.dataset.option });
        toast(res.message);
      } catch (e) {
        toast(e.message || "That decision could not be made.", true);
      }
      await window.refreshAll();
    });
  });
}

// ---------------------------------------------------------------- Goal ladder

async function renderGoalsInto(host) {
  let g;
  try { g = await ceoGet("/goals"); } catch (e) { host.innerHTML = ""; return; }
  host.innerHTML = `
    <div class="goals-head"><h3 class="inbox-title">🎯 Next goals</h3>
      <span class="inbox-count">${g.completed} of ${g.total} achieved</span></div>
    <div class="goals-list">${g.next.map((goal) => `
      <div class="goal">
        <div class="goal-title">${esc(goal.title)}</div>
        <div class="goal-detail">${esc(goal.detail)}</div>
        ${goal.progress_pct !== null ? `<div class="goal-bar"><div class="goal-bar-fill" style="width:${goal.progress_pct}%"></div></div>` : ""}
      </div>`).join("")}</div>`;
}

// ---------------------------------------------------------------- Overview hook

/**
 * Overview keeps the company picture; the CEO's own work (Inbox, Agenda,
 * goals, delegation) lives in the CEO Office tab. Overview just
 * says what's waiting there, one tap away.
 */
const _ceoOriginalOverview = window.renderOverview;
window.renderOverview = async function (main, snap) {
  await _ceoOriginalOverview(main, snap);
  if (getCurrentTab() !== "overview") return;
  const title = main.querySelector(".panel-title");
  const wrap = document.createElement("section");
  wrap.className = "ceo-call";
  if (title) title.insertAdjacentElement("afterend", wrap); else main.prepend(wrap);
  await renderOfficeCallInto(wrap);
};

async function renderOfficeCallInto(host) {
  const [box, agenda, mom, cq] = await Promise.all([ceoGet("/inbox").catch(() => null), ceoGet("/ceo-agenda").catch(() => null),
    ceoGet("/moments").catch(() => null), ceoGet("/consequences").catch(() => null)]);
  const comeBack = cq ? (cq.waiting || []).length : 0;
  const waiting = box ? (box.open || []).length : 0;
  const invites = agenda ? (agenda.invitations || []).length : 0;
  const mom2 = mom && mom.moment ? mom : null;
  const live = mom2 && mom2.current;
  const tile = (icon, label, value, hot, sub) => `<button type="button" class="ceo-call-tile${hot ? " hot" : ""}" data-office="${label}"><span>${icon}</span><b>${esc(value)}</b><em>${esc(sub)}</em></button>`;
  host.innerHTML = `
    <div class="ceo-call-head"><div><h3>🎩 CEO Office</h3><p>${live ? `${esc(mom2.moment.title)} is on: make your call.` : waiting || invites || comeBack ? "Things are waiting for you." : "Nothing urgent. Your desk is clear."}</p></div>
      <button type="button" class="primary" data-office="open">Open the CEO Office</button></div>
    <div class="ceo-call-tiles">
      ${mom2 ? tile(mom2.moment.icon, "moments", live ? `${mom2.moment.title}: now` : mom2.moment.title, !!live, live ? "your industry's big moment" : `next in month ${mom2.next_month}`) : ""}
      ${tile("📥", "inbox", `${waiting} decision${waiting === 1 ? "" : "s"}`, waiting > 0, "in your Inbox")}
      ${tile("🎙", "agenda", `${invites} invitation${invites === 1 ? "" : "s"}`, false, "on your Agenda")}
      ${tile("🧠", "consequences", comeBack ? `${comeBack} story${comeBack === 1 ? "" : "s"} came back` : `${cq && cq.summary ? cq.summary.active : 0} running`, comeBack > 0, "decisions that come back")}
    </div>`;
  host.querySelectorAll("[data-office]").forEach((b) => b.addEventListener("click", () => {
    const k = b.dataset.office;
    if (k === "consequences") return showTab("consequences");
    if (k !== "open") { try { localStorage.setItem("ges.office.tab", k); } catch (e) { /* not remembered */ } }
    showTab("office");
  }));
}

/** What each delegation mode means, in plain words. */
const DELEGATION_MODES = {
  manual: { label: "Manual", icon: "🙋", what: "You make every decision in this department yourself. Nothing happens without you.",
            when: "Best when nobody is in the seat yet, or the executive is weak (skill under 50)." },
  advise: { label: "Advise", icon: "💬", what: "Decisions still come to you, but your executive recommends an option on each card (marked in your Inbox).",
            when: "Best for a solid executive (skill 50-69): you keep control and get a second opinion." },
  autopilot: { label: "Autopilot", icon: "🤖", what: "Your executive decides for you, the moment a decision arrives, with their real skill and personality. A weak hire makes weak calls.",
               when: "Best for a strong executive (skill 70+) on a department you don't want to micromanage." },
};

/** The mode we'd suggest for a department, from the executive in the seat. */
function suggestDelegation(r) {
  if (!r.executive) return { mode: "manual", why: "No one holds this seat, so it stays with you." };
  const k = r.executive.skill;
  if (k >= 70) return { mode: "autopilot", why: `${r.executive.name.split(" ")[0]} is strong (skill ${k}): let them run it.` };
  if (k >= 50) return { mode: "advise", why: `${r.executive.name.split(" ")[0]} is solid (skill ${k}): take their advice, keep the final say.` };
  return { mode: "manual", why: `${r.executive.name.split(" ")[0]} is still learning (skill ${k}): decide these yourself.` };
}

async function renderDelegationInto(host) {
  let rows;
  try { rows = await ceoGet("/delegation"); } catch (e) { host.innerHTML = ""; return; }
  host.innerHTML = `
    <h2 class="panel-title" style="margin-top:28px;">Delegation</h2>
    <p class="delegation-intro">Choose how much each department runs itself. Every decision your departments raise lands in the CEO Inbox; the mode decides who answers it.</p>
    <div class="delegation-legend">${Object.entries(DELEGATION_MODES).map(([k, m]) => `
      <div class="dg-mode dg-${k}"><b>${m.icon} ${m.label}</b><span>${esc(m.what)}</span><em>${esc(m.when)}</em></div>`).join("")}</div>
    <div class="delegation-rows">${rows.map((r) => {
      const sug = suggestDelegation(r);
      return `
      <div class="delegation-row">
        <div class="delegation-dept">${esc(r.label)}
          <div class="delegation-exec">${r.executive
            ? `${esc(r.executive.name)} · ${esc(shortRole(r.executive.role))} · skill ${r.executive.skill}/100 · ${esc(r.executive.style)}`
            : esc(r.note || "")}</div>
          <div class="delegation-suggest ${sug.mode === r.mode ? "ok" : ""}">${sug.mode === r.mode ? "✓ " : "Suggested: "}<b>${DELEGATION_MODES[sug.mode].label}</b> · ${esc(sug.why)}</div>
        </div>
        <div class="delegation-modes" role="group" aria-label="${esc(r.label)} delegation">
          ${["manual", "advise", "autopilot"].map((m) => `
            <button type="button" class="delegation-mode${r.mode === m ? " active" : ""}${sug.mode === m ? " suggested" : ""}" data-dept="${esc(r.department)}" data-mode="${m}"
              title="${esc(DELEGATION_MODES[m].what)}" ${!r.executive && m !== "manual" ? "disabled" : ""}>${DELEGATION_MODES[m].label}</button>`).join("")}
        </div>
      </div>`;
    }).join("")}</div>`;
  host.querySelectorAll(".delegation-mode").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const res = await postJSON("/delegation", { department: btn.dataset.dept, mode: btn.dataset.mode });
      toast(res.message);
      await renderDelegationInto(host);
    });
  });
}

// ---------------------------------------------------------------- Progressive unlocks

function showAllTabs() {
  try { return localStorage.getItem(CEO_SHOW_ALL_KEY) === "1"; } catch (e) { return false; }
}

const ceoTabLabel = (tab) => {
  const b = document.querySelector(`.sidebar .tab[data-tab="${tab}"] .label-text`);
  return b ? b.textContent.trim() : tab;
};

/** Under the menu: how many sections are still to come, and the next milestones. */
function ceoUnlockHint(all) {
  const sidebar = document.querySelector(".sidebar");
  if (!sidebar) return;
  let hint = document.getElementById("unlock-hint");
  const locked = Object.entries(ceoUnlocks.tabs).filter(([t, v]) => !v.unlocked && document.querySelector(`.sidebar .tab[data-tab="${t}"]`));
  if (all || !locked.length) { if (hint) hint.remove(); return; }
  if (!hint) {
    hint = document.createElement("div");
    hint.id = "unlock-hint";
    hint.className = "unlock-hint";
    const toggle = document.getElementById("show-all-tabs");
    sidebar.insertBefore(hint, toggle || null);
  }
  const next = locked.slice(0, 3).map(([t, v]) => `<li><b>${esc(ceoTabLabel(t))}</b>: ${esc((v.condition || "").replace(/ \(Or wait until month \d+\.\)$/, ""))}</li>`).join("");
  hint.innerHTML = `<div>🔒 <b>${locked.length} more section${locked.length > 1 ? "s" : ""}</b> unlock as you grow (all by month 6).</div><ul>${next}</ul>`;
}

/** A toast when a milestone opens new sections (not on the first load). */
let ceoSeenUnlocked = null;
// what each section is for, in one line, the first time it opens (2026-10-24)
const TAB_INTRO = {
  ambassadors: "Advertise your products: pick a message, an audience and a channel.",
  competitors: "Your rivals: their share, their moves, and how to answer them.",
  world: "Your company on the world map: where you sell and build.",
  lifestyle: "Spend what you earn: homes, cars, travel. It's your money, not the company's.",
  deals: "Meet companies and sign partnerships that bring in revenue.",
  banking: "Borrow from banks: rates follow your credit rating.",
  rnd: "Research new technology; finished research becomes new products.",
  workforce: "Everyone below the executives: morale, pay, unions, and how many people you need.",
  consulting: "An outside review of the company: what to fix first.",
  board: "The directors who judge your results every quarter.",
  risk: "Incidents, insurance, compliance and your regulators.",
  consequences: "Your past decisions coming back: answer them.",
  investors: "Who owns the company, your pay, and the dividend.",
  investments: "Invest in other companies, and offers made to you.",
  esg: "Emissions, the carbon price and your ESG grade.",
  dream: "Buy a real sports team with your own money or the company's.",
  countries: "Every country you operate in, as an investment, and the companies you own.",
  macro: "The world economy: growth, interest rates, currencies and the news.",
  government: "Governments invite you to invest; their incentives depend on their real economy.",
  ma: "Buy other companies: targets, due diligence, integration.",
  publicmarkets: "Your shares on the market, the IPO, buybacks and splits.",
  ranking: "Where you stand among real companies and other players.",
  footprint: "Where your company has a real presence.",
};

function ceoAnnounceUnlocks() {
  const open = Object.entries(ceoUnlocks.tabs).filter(([, v]) => v.unlocked).map(([t]) => t)
    .filter((t) => document.querySelector(`.sidebar .tab[data-tab="${t}"]`));
  if (ceoSeenUnlocked) {
    const fresh = open.filter((t) => !ceoSeenUnlocked.has(t));
    // one note for everything that opened at once (2.7: three stacked toasts covered a phone's whole screen)
    if (fresh.length === 1) toast(`🔓 New: ${ceoTabLabel(fresh[0])}${TAB_INTRO[fresh[0]] ? `: ${TAB_INTRO[fresh[0]]}` : ""}`, false, 7000);
    else if (fresh.length > 1) toast(`🔓 New sections: ${fresh.map(ceoTabLabel).join(", ")}. They're in the menu now.`, false, 8000);
  }
  ceoSeenUnlocked = new Set(open);
}

function applyUnlocks() {
  if (!ceoUnlocks) return;
  const all = showAllTabs();
  document.querySelectorAll(".sidebar .tab, .mobile-sheet-item").forEach((btn) => {
    const info = ceoUnlocks.tabs[btn.dataset.tab];
    // Boolean(...) matters: a tab with no unlock rule (R&D Lab, Marketing, Workforce, ESG, League...) gave
    // `undefined`, and classList.toggle("locked", undefined) FLIPS the class, so those tabs switched between
    // locked and open on every refresh (seen on the live site at month 12).
    const locked = Boolean(!all && info && !info.unlocked);
    btn.classList.toggle("locked", locked);
    // 2026-10-23: locked sections are hidden, not greyed (a new player saw 32 tabs on day one)
    btn.classList.toggle("tab-hidden", locked);
    btn.setAttribute("aria-disabled", locked ? "true" : "false");
    btn.title = locked ? `🔒 ${info.condition}` : "";
  });
  // a group heading with nothing open under it is hidden too
  document.querySelectorAll(".sidebar .tab-group-label").forEach((label) => {
    let el = label.nextElementSibling, any = false;
    while (el && !el.classList.contains("tab-group-label")) {
      if (el.classList.contains("tab") && !el.classList.contains("tab-hidden")) any = true;
      el = el.nextElementSibling;
    }
    label.classList.toggle("tab-hidden", !any);
  });
  ceoUnlockHint(all);
  ceoAnnounceUnlocks();
  let toggle = document.getElementById("show-all-tabs");
  const sidebar = document.querySelector(".sidebar");
  if (!toggle && sidebar) {
    toggle = document.createElement("label");
    toggle.id = "show-all-tabs";
    toggle.className = "show-all-tabs";
    toggle.innerHTML = `<input type="checkbox"> Show all sections`;
    sidebar.appendChild(toggle);
    toggle.querySelector("input").addEventListener("change", (e) => {
      try { localStorage.setItem(CEO_SHOW_ALL_KEY, e.target.checked ? "1" : "0"); } catch (err) { /* private mode */ }
      applyUnlocks();
    });
  }
  if (toggle) toggle.querySelector("input").checked = all;
}

// A locked tab explains itself instead of opening (capture phase, so this
// runs before the sidebar button's own handler).
document.addEventListener("click", (e) => {
  const btn = e.target.closest(".sidebar .tab.locked, .mobile-sheet-item.locked");
  if (!btn || !e.isTrusted) return;   // in-game links (alerts' "View →") still open the tab
  e.preventDefault();
  e.stopImmediatePropagation();
  toast(btn.title || "Not unlocked yet.");
}, true);

async function refreshUnlocks() {
  try { ceoUnlocks = await ceoGet("/unlocks"); } catch (e) { ceoUnlocks = null; }
  applyUnlocks();
}

const _ceoOriginalRefreshAll = window.refreshAll;
window.refreshAll = async function () {
  await _ceoOriginalRefreshAll();
  await refreshUnlocks();
};

// the mobile sheet is rebuilt on every open; keep its lock state in sync
const _ceoOriginalSync = window.syncMobileNav;
window.syncMobileNav = function () {
  _ceoOriginalSync();
  applyUnlocks();
};
document.addEventListener("click", (e) => {
  if (e.target.closest("#mobile-nav .mobile-nav-btn")) setTimeout(applyUnlocks, 0);
});

// ---------------------------------------------------------------- Month-close summary

// month close: the game's compact money format ($2.40M), so big numbers stay readable on a phone
// (tests load this file without its imports: fall back to the plain format there)
const mcMoney = (v) => (typeof uiMoney === "function" ? uiMoney(v, { compact: true }) : fmtMoney(v));

function deltaHtml(now, before, money = true) {
  if (before === undefined || before === null) return "";
  const d = now - before;
  if (Math.abs(d) < 0.5) return `<span class="mc-delta">±0</span>`;
  const txt = money ? mcMoney(Math.abs(d)) : Math.abs(d).toFixed(0);
  return `<span class="mc-delta ${d > 0 ? "pos" : "neg"}">${d > 0 ? "▲" : "▼"} ${txt}</span>`;
}

/**
 * Revenue, profit, cash and valuation now vs. `before`, plus a "Why?" under the valuation that asks the
 * server which causes moved it (GET /api/valuation/change). Used by Month close and Fast Forward.
 * Rachit (2026-10-23): "when we skip a month, show how cash, revenue and valuation changed; a why
 * button on the valuation that tells the cause".
 */
function changeGridHtml(snap, before) {
  const is = snap.income_statement, bs = snap.balance_sheet, r = snap.ratios || {};
  const pis = before && before.income_statement, pbs = before && before.balance_sheet, pr = before && before.ratios;
  const valued = r.valuation_status !== "not_valued";
  // every rise or fall gets its reason (month_why.py; the valuation's comes from valuation_view.py)
  const whyBtn = (metric) => (before ? `<button type="button" class="mc-why" data-mc-why2="${metric}:${before.month}:${snap.month}" aria-expanded="false">Why?</button>` : "");
  return `<div class="mc-grid">
      <div class="mc-stat"><div class="mc-label">Revenue</div><div class="mc-value">${mcMoney(is.revenue)}</div>${deltaHtml(is.revenue, pis && pis.revenue)}${whyBtn("revenue")}</div>
      <div class="mc-stat"><div class="mc-label">Net income</div><div class="mc-value ${is.net_income < 0 ? "neg" : "pos"}">${mcMoney(is.net_income)}</div>${deltaHtml(is.net_income, pis && pis.net_income)}${whyBtn("net_income")}</div>
      <div class="mc-stat"><div class="mc-label">Cash</div><div class="mc-value">${mcMoney(bs.CASH)}</div>${deltaHtml(bs.CASH, pbs && pbs.CASH)}${whyBtn("cash")}</div>
      <div class="mc-stat"><div class="mc-label">Valuation</div><div class="mc-value">${valued ? mcMoney(r.valuation) : "Not valued yet"}</div>
        ${valued ? deltaHtml(r.valuation, pr && pr.valuation) : ""}
        ${valued && before ? `<button type="button" class="mc-why" data-mc-why="${before.month}:${snap.month}" aria-expanded="false">Why?</button>` : ""}</div>
    </div>
    <div class="mc-why-panel" hidden></div>`;
}

function valuationWhyHtml(ch) {
  if (!ch.causes || !ch.causes.length) return `<p class="mc-why-sum">${esc(ch.summary)}</p>`;
  const top = Math.max(...ch.causes.map((c) => Math.abs(c.delta))) || 1;
  return `<p class="mc-why-sum">${esc(ch.summary)}</p>
    <ul class="mc-why-list">${ch.causes.map((c) => `<li>
      <div class="mc-why-row"><span class="mc-why-label">${esc(c.label)}</span>
        <span class="mc-delta ${c.delta > 0 ? "pos" : "neg"}">${c.delta > 0 ? "▲" : "▼"} ${mcMoney(Math.abs(c.delta))}</span></div>
      <div class="mc-why-bar"><span class="${c.delta > 0 ? "pos" : "neg"}" style="width:${Math.max(3, (100 * Math.abs(c.delta)) / top).toFixed(1)}%"></span></div>
      <div class="mc-why-text">${esc(c.why)}</div></li>`).join("")}</ul>
    ${(ch.fixes || []).length ? `<div class="mc-why-fix"><div class="mc-label">How to raise it again</div>
      <ul>${ch.fixes.map((f) => `<li><b>${esc(f.label)}:</b> ${esc(f.fix)}</li>`).join("")}</ul></div>` : ""}`;
}

function wireChangeGrid(root) {
  root.querySelectorAll("[data-mc-why2]").forEach((b) => b.addEventListener("click", async () => {
    const panel = root.querySelector(".mc-why-panel");
    if (!panel) return;
    const [metric, from, to] = b.dataset.mcWhy2.split(":");
    if (!panel.hidden && panel.dataset.shown === metric) { panel.hidden = true; return; }
    panel.hidden = false;
    panel.dataset.shown = metric;
    panel.dataset.loaded = "";
    panel.innerHTML = `<p class="mc-why-sum">Working out what moved it…</p>`;
    try { panel.innerHTML = valuationWhyHtml(await ceoGet(`/month/why?metric=${metric}&from_month=${from}&to_month=${to}`)); }
    catch (e) { panel.innerHTML = `<p class="mc-why-sum">Couldn't load the breakdown. Try again.</p>`; }
  }));
  const btn = root.querySelector("[data-mc-why]");
  const panel = root.querySelector(".mc-why-panel");
  if (!btn || !panel) return;
  btn.addEventListener("click", async () => {
    const open = panel.hidden;
    panel.hidden = !open;
    btn.setAttribute("aria-expanded", String(open));
    btn.textContent = open ? "Hide" : "Why?";
    if (!open || panel.dataset.loaded) return;
    const [from, to] = btn.dataset.mcWhy.split(":");
    panel.innerHTML = `<p class="mc-why-sum">Working out what moved it…</p>`;
    try {
      panel.innerHTML = valuationWhyHtml(await ceoGet(`/valuation/change?from_month=${from}&to_month=${to}`));
      panel.dataset.loaded = "1";
    } catch (e) {
      panel.innerHTML = `<p class="mc-why-sum">Couldn't load the breakdown. Try again.</p>`;
    }
  });
}

/** Month close: the four numbers count up from last month's to this month's (light: text only, ~0.7 s). */
function countUpGrid(root, snap, before) {
  if (!before || typeof requestAnimationFrame !== "function" || typeof performance === "undefined") return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const pairs = [[snap.income_statement.revenue, before.income_statement.revenue], [snap.income_statement.net_income, before.income_statement.net_income],
    [snap.balance_sheet.CASH, before.balance_sheet.CASH], [(snap.ratios || {}).valuation, (before.ratios || {}).valuation]];
  root.querySelectorAll(".mc-stat .mc-value").forEach((el, i) => {
    const [to, from] = pairs[i] || [];
    if (typeof to !== "number" || typeof from !== "number" || !isFinite(to) || !isFinite(from) || el.textContent.includes("Not valued")) return;
    const t0 = performance.now(), dur = 700;
    const step = (t) => {
      const k = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - k, 3);
      el.textContent = mcMoney(from + (to - from) * e);
      if (k < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  });
}

/** Big firsts get a moment on screen: first profit, a $1B valuation, $1B a year in revenue, going public. Once per company. */
const CELEBRATIONS = [
  { key: "first_profit", test: (s) => s.income_statement.net_income > 0, icon: "🎉", title: "Your first profitable month", line: "The company paid for itself this month. Most startups take years to get here." },
  { key: "unicorn", test: (s) => ((s.ratios || {}).valuation || 0) >= 1e9, icon: "🦄", title: "A billion-dollar company", line: "Your valuation passed $1 billion: you're a unicorn." },
  { key: "rev_1b", test: (s) => s.income_statement.revenue * 12 >= 1e9, icon: "🏆", title: "A billion dollars a year", line: "Revenue is running above $1 billion a year." },
  { key: "public", test: () => !!(window.__company && window.__company.is_public), icon: "🔔", title: "You're a public company", line: "Your shares trade on the market. Every quarter now matters to investors." },
];

async function celebrate(snap) {
  const co = window.__company || {};
  const store = `ges.celebrated.${co.name || "co"}.${co.seed || ""}`;
  let done = {};
  try { done = JSON.parse(localStorage.getItem(store) || "{}"); } catch (e) { /* not remembered */ }
  let c = CELEBRATIONS.find((x) => !done[x.key] && x.test(snap));
  if (!c) {  // this month's moments from the server: a title for your team, a customer doubling its order
    try {
      const m = await ceoGet(`/milestones?month=${snap.month}`);
      c = (m.moments || []).find((x) => !done[x.key]) || null;
    } catch (e) { /* optional */ }
  }
  if (!c) return Promise.resolve();
  done[c.key] = snap.month;
  try { localStorage.setItem(store, JSON.stringify(done)); } catch (e) { /* not remembered */ }
  sfx("win");
  const el = document.createElement("div");
  el.className = "celebrate";
  el.setAttribute("role", "status");
  el.innerHTML = `<div class="celebrate-card"><div class="celebrate-ico" aria-hidden="true">${c.icon}</div><b>${esc(c.title)}</b><p>${esc(c.line)}</p>
    <button type="button" class="primary">Onwards</button></div>`;
  document.body.appendChild(el);
  return new Promise((resolve) => {
    const close = () => { if (el.isConnected) { el.remove(); resolve(); } };
    el.querySelector("button").addEventListener("click", close);
    el.addEventListener("click", (e) => { if (e.target === el) close(); });
    setTimeout(close, 6000);
  });
}

window.gesChangeGridHtml = changeGridHtml;
window.gesValuationWhyHtml = valuationWhyHtml; // Public Markets: why the share price moved
window.gesWireChangeGrid = wireChangeGrid;

// ---------------------------------------------------------------- Quarterly dividend decision

/**
 * After a quarter closes: how much of the profit to pay shareholders. Says in plain words what they expect
 * and why, the slider with your share and theirs, and how the market will take it (GET /api/dividend/quarter,
 * action quarter_dividend in capital.py). Rachit (2026-10-24): "every quarter a pop-up for the dividend,
 * simply tell what shareholders want, and a guide button".
 */
async function showQuarterDividend() {
  let q;
  try { q = await ceoGet("/dividend/quarter"); } catch (e) { return; }
  if (!q || !q.ask) return;
  const top = Math.max(1000, Math.floor(q.payable / 1000) * 1000);
  const step = Math.max(1000, Math.round(top / 200 / 1000) * 1000);
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal dv-modal" role="dialog" aria-labelledby="dv-title">
      <button type="button" class="modal-x" data-dv-x aria-label="Close">✕</button>
      <div class="mc-eyebrow">${esc(q.quarter_label)} closed · dividend decision</div>
      <h3 id="dv-title">How much do you pay your shareholders?</h3>
      <div class="dv-facts">
        <div><span>Profit this quarter</span><b class="${q.profit < 0 ? "neg" : "pos"}">${fmtMoney(q.profit)}</b></div>
        <div><span>Revenue growth (year)</span><b>${q.growth_pct === null ? "—" : `${q.growth_pct}%`}</b></div>
        <div><span>Most you can pay</span><b>${fmtMoney(q.payable)}</b></div>
      </div>
      <div class="dv-expect"><b>Your shareholders expect: ${q.expected > 0 ? fmtMoney(q.expected) : "nothing this quarter"}</b><p>${esc(q.who)}</p></div>
      <label class="dv-slide">Pay out <b id="dv-amt">${fmtMoney(q.amount)}</b>
        <input type="range" id="dv-range" min="0" max="${top}" step="${step}" value="${Math.min(top, Math.round(q.expected / step) * step)}" aria-label="Dividend amount"></label>
      <div class="dv-chips"><button type="button" class="mv-chip" data-dv-set="0">Nothing</button>
        ${q.expected > 0 ? `<button type="button" class="mv-chip" data-dv-set="${q.expected}">What they expect</button>` : ""}
        ${q.profit > 0 ? `<button type="button" class="mv-chip" data-dv-set="${Math.min(top, Math.round(q.profit * 0.5))}">Half the profit</button>` : ""}</div>
      <div class="dv-split" id="dv-split"></div>
      <p class="dv-react" id="dv-react" aria-live="polite"></p>
      <details class="dv-guide"><summary>❓ Guide: what is a dividend?</summary>
        <p>A dividend is part of the company's profit handed to the people who own its shares. ${esc(q.holders)}</p>
        <p>Paying one lowers the company's cash (and its valuation by the same amount) but rewards owners. Young, fast-growing
          companies usually pay nothing and reinvest every dollar; mature companies pay a steady share of profit, and the market
          punishes a cut. Paying more than the company earns worries investors.</p>
        <p>Want a fixed amount every quarter? Make a dividend promise in Investors → Shareholder moves.</p></details>
      <div class="modal-actions"><button type="button" data-dv-skip>Pay nothing</button>
        <button type="button" class="primary" id="dv-go">Pay the dividend</button></div>
    </div>`;
  document.body.appendChild(backdrop);
  const range = backdrop.querySelector("#dv-range"), go = backdrop.querySelector("#dv-go");
  let timer = 0;
  const update = () => {
    const a = +range.value;
    backdrop.querySelector("#dv-amt").textContent = fmtMoney(a);
    go.textContent = a > 0 ? `Pay ${fmtMoney(a)}` : "Pay nothing";
    clearTimeout(timer);
    timer = setTimeout(async () => {
      try {
        const r = await ceoGet(`/dividend/quarter?amount=${a}`);
        backdrop.querySelector("#dv-split").innerHTML = a > 0
          ? `<span>To you <b>${fmtMoney(r.to_you)}</b></span><span>To other shareholders <b>${fmtMoney(r.to_others)}</b></span>${r.payout_ratio_pct !== null ? `<span>${r.payout_ratio_pct}% of the profit</span>` : ""}`
          : `<span>The profit stays in the company.</span>`;
        const el = backdrop.querySelector("#dv-react");
        el.className = `dv-react ${r.reaction > 0 ? "pos" : r.reaction < 0 ? "neg" : ""}`;
        el.textContent = `${r.reaction > 0 ? "📈" : r.reaction < 0 ? "📉" : "➖"} ${r.reaction_text}${r.reaction ? ` (investor mood ${r.reaction > 0 ? "+" : ""}${r.reaction})` : ""}`;
      } catch (e) { /* the slider still works */ }
    }, 160);
  };
  range.addEventListener("input", update);
  backdrop.querySelectorAll("[data-dv-set]").forEach((b) => b.addEventListener("click", () => { range.value = b.dataset.dvSet; update(); }));
  update();
  const decide = async (amount) => {
    go.disabled = true;
    try {
      const r = await postJSON("/actions/execute", { action: { type: "quarter_dividend", params: { amount } } });
      toast(r.message);
    } catch (e) { toast(e.message, true); go.disabled = false; return; }
    backdrop.remove();
    await window.refreshAll();
  };
  go.addEventListener("click", () => decide(+range.value));
  backdrop.querySelector("[data-dv-skip]").addEventListener("click", () => decide(0));
  // closing without choosing leaves the decision open: Investors shows it under Needs your attention
  backdrop.querySelector("[data-dv-x]").addEventListener("click", () => backdrop.remove());
}

window.showQuarterDividend = showQuarterDividend;

window.showMonthClose = async function (narrative, before) {
  const snap = window.__snapshot;
  if (!snap) { showReportModal(narrative); return; }
  let box = { open: [] }, goals = { next: [], completed: 0, total: 0 }, mom = null;
  try { [box, goals] = await Promise.all([ceoGet("/inbox"), ceoGet("/goals")]); } catch (e) { /* summary still shows the numbers */ }
  try { mom = await ceoGet("/moments"); if (!mom || !mom.moment) mom = null; } catch (e) { /* optional */ }
  const quarter = snap.month % 3 === 0;
  sfx("month");
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal month-close">
      <div class="mc-eyebrow">${quarter ? "Quarter close" : "Month close"}</div>
      <h3>Month ${snap.month} is in the books</h3>
      ${changeGridHtml(snap, before)}
      <div class="mc-line">📥 ${box.open.length ? `<strong>${box.open.length} decision${box.open.length > 1 ? "s" : ""}</strong> waiting in your Inbox` : "No decisions waiting"}</div>
      ${snap.month % 12 === 0 ? `<div class="mc-line mc-hot">🎁 <strong>Your Year ${snap.month / 12} Wrapped</strong> is ready <button type="button" class="mc-wrapped" id="mc-wrapped">Play it</button></div>` : ""}
      ${mom && mom.current ? `<div class="mc-line mc-hot">${esc(mom.moment.icon)} <strong>${esc(mom.moment.title)}</strong> is on this month: make your call in the CEO Office</div>` : ""}
      ${quarter ? `<div class="mc-line mc-hot">💰 <strong>Quarter closed:</strong> next, decide this quarter's dividend for your shareholders</div>` : ""}
      ${goals.next.length ? `<div class="mc-line">🎯 Next goal: <strong>${esc(goals.next[0].title)}</strong> · ${goals.completed}/${goals.total} achieved</div>` : ""}
      <details class="mc-report"><summary>Read the full Board Report</summary><div class="report-narrative report">${esc(narrative || "")}</div></details>
      <div class="modal-actions">
        <button id="mc-close">Close</button>
        <button class="primary" id="mc-inbox">${box.open.length ? "Open Inbox" : "Continue"}</button>
      </div>
    </div>`;
  document.body.appendChild(backdrop);
  wireChangeGrid(backdrop);
  countUpGrid(backdrop, snap, before);
  // one thing at a time (2.7): a celebration waits until the month close is put away, then the quarter's dividend
  const close = () => {
    if (!backdrop.isConnected) return;
    backdrop.remove();
    setTimeout(() => celebrate(snap).catch(() => {}).then(() => { if (quarter) showQuarterDividend(); }), 250);
  };
  backdrop.querySelector("#mc-close").addEventListener("click", close);
  backdrop.querySelector("#mc-inbox").addEventListener("click", () => {
    close();
    if (!(mom && mom.current)) { try { localStorage.setItem("ges.office.tab", "inbox"); } catch (e) { /* not remembered */ } }
    showTab("office");
  });
  backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
  if (mom && mom.current) {
    backdrop.querySelector("#mc-inbox").textContent = `Go to ${mom.moment.title}`;
    try { localStorage.setItem("ges.office.tab", "moments"); } catch (e) { /* not remembered */ }
  }
  const wr = backdrop.querySelector("#mc-wrapped");
  if (wr) wr.addEventListener("click", () => { close(); if (window.openWrapped) window.openWrapped(snap.month / 12); });
  // the quarterly Board vote: a short cinematic over the summary (skippable)
  if (quarter) ceoGet("/board/desk").then((desk) => { if (desk && (desk.directors || []).length) cinemaBoardVote(desk); }).catch(() => {});
};

// app.js may already have rendered before this file loaded
if (window.__snapshot) { renderTab(); refreshUnlocks(); }

export { renderDelegationInto, renderGoalsInto, renderInboxInto, renderOfficeCallInto };
